/**
 * The product areas a ticket can be filed against.
 *
 * This list is the vocabulary. Nothing else defines one: ticket-service
 * validates productScope as non-empty and nothing more, so before this the
 * field was free text and every caller invented its own spelling — the seeder
 * used PMS/WBS/IAM, the form's placeholder suggested "DISTRIBUTION, PMS,
 * WALLET…", and the tests used DISTRIBUTION.
 *
 * That mattered more than a tidiness complaint, because SLA policy resolution
 * matches on this value:
 *
 *   AND product_scope IN (?, '*')
 *
 * A scope that matches no policy does not error. It falls through to the
 * wildcard policy and the ticket quietly gets a different SLA than the one
 * intended, with nothing anywhere reporting that a substitution happened. A
 * typo was therefore not a cosmetic defect; it silently reassigned the
 * deadline the ticket would be judged against.
 *
 * `value` is what crosses the wire and must stay uppercase to match the
 * existing rows and the seeder. `label` is an English translation key, not
 * display text — it is passed through getTranslate at render.
 */
export interface TicketProductScope {
  value: string
  label: string
}

export const TICKET_PRODUCT_SCOPES: readonly TicketProductScope[] = [
  { value: 'DISTRIBUTION', label: 'Distribution' },
  { value: 'PMS', label: 'PMS' },
  { value: 'WALLET', label: 'Wallet' },
  { value: 'IAM', label: 'IAM' },
  { value: 'WBS', label: 'WBS' },
  { value: 'ASSET', label: 'Asset' },
  { value: 'REPORTING', label: 'Reporting' }
]

// A companion isKnownProductScope() was written here and removed before merge.
// It had no caller: the surface that needs it is one that READS a scope back —
// a detail or edit screen rendering a ticket created before this list existed,
// or by another client, since the backend still accepts any non-empty string.
// No such screen is in this change, so it was dead code shipped on the promise
// of a future caller. It belongs in the MR that introduces that caller, where
// its behaviour can be verified rather than assumed.
